import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { TelSysIntegrationMode } from '../../Common/Types/Types'
import { BUSINESS_OPTION, BusinessOption, INTEGRATION_TYPE, IntegrationType, TelSysTypes } from '../Constants/Constants'

/**
 * <p>
 *     Parameters describing the current state of a telephony-system configuration form, used to
 *     decide whether the Business CX options need seeding with a sensible default.
 * </p>
 */
export type BusinessRealTimeOptionsParams = {
    /** The currently selected telephony-system type. */
    telSysType: TelSysTypes
    /** The currently selected integration type, if any. */
    integrationType: Definable<IntegrationType>
    /** The currently selected integration mode, if any. */
    integrationMode: Definable<TelSysIntegrationMode>
    /** The business CX options currently held in the form, if any. */
    currentBusinessOptions: Nullable<BusinessOption[]>
}

/**
 * <p>
 *     Returns the default Business CX options for an MS Teams telsys sitting in Business / Real-Time
 *     integration with no options selected, or {@code null} when no seeding is required.
 * </p>
 *
 * <p>
 *     A Business Real-Time telsys must carry at least one CX option or the save is rejected
 *     server-side (options default to {@code None}, which is invalid for this mode). The options are
 *     otherwise only set by the Integration Type change handler, so two paths leave them empty: a new
 *     telsys accepting the Business default (APD-13306), and switching Historic -> Real-Time (which
 *     first clears the options). This mirrors the change handler's Real-Time default so a valid,
 *     non-empty option set is always present. Existing options and non-Business / non-Real-Time /
 *     non-Teams states are left untouched, so it never overwrites a user's selection.
 * </p>
 *
 * @param params The current form state - see {@link BusinessRealTimeOptionsParams}.
 *
 * @return The options to seed, or {@code null} when the current state should be left as-is.
 */
export const getDefaultBusinessRealTimeOptions = ({
    telSysType,
    integrationType,
    integrationMode,
    currentBusinessOptions
}: BusinessRealTimeOptionsParams): Nullable<BusinessOption[]> => {
    const shouldSeedDefaults: boolean =
        telSysType === TelSysTypes.MS_TEAMS &&
        integrationType === INTEGRATION_TYPE.BUSINESS &&
        integrationMode === TelSysIntegrationMode.REAL_TIME &&
        IsVarEmpty(currentBusinessOptions)

    return shouldSeedDefaults ? [BUSINESS_OPTION.CX_ANALYTICS, BUSINESS_OPTION.CX_INTEGRATION] : null
}
